import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Bell, MessageSquare, Ticket, ChevronRight, Loader2 } from 'lucide-react';

export default function NotificationBell() {
  const { authAxios, isAuthenticated, userType } = useAuth();
  const navigate = useNavigate();
  const [messageCount, setMessageCount] = useState(0);
  const [ticketCount, setTicketCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchCounts = useCallback(async () => {
    if (!isAuthenticated || userType !== 'candidate') return;
    
    try {
      // Fetch message unread count
      const msgResponse = await authAxios.get('/candidates/messages/unread-count');
      setMessageCount(msgResponse.data.unread_count || 0);
      
      // Fetch ticket unread count
      try {
        const ticketResponse = await authAxios.get('/candidates/support/tickets/unread/count');
        setTicketCount(ticketResponse.data.unread_count || 0);
      } catch (e) {
        // Endpoint might not exist yet
        setTicketCount(0);
      }
    } catch (error) {
      console.error('Failed to fetch notification counts:', error);
    }
  }, [authAxios, isAuthenticated, userType]);

  useEffect(() => {
    fetchCounts();
    
    // Poll every 60 seconds for new notifications
    const interval = setInterval(fetchCounts, 60000);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  if (!isAuthenticated || userType !== 'candidate') return null;

  const totalCount = messageCount + ticketCount;

  const handleNavigate = (path) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5 text-slate-600" />
          {totalCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-lime-500 text-white text-xs flex items-center justify-center font-medium animate-pulse">
              {totalCount > 9 ? '9+' : totalCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="end">
        <div className="p-3 border-b border-slate-100">
          <h4 className="font-semibold text-slate-900 text-sm">Notifications</h4>
        </div>
        <div className="p-2">
          {/* Messages */}
          <button
            onClick={() => handleNavigate('/messages')}
            className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
                <MessageSquare className="h-4 w-4 text-blue-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-slate-900">Messages</p>
                <p className="text-xs text-slate-500">
                  {messageCount > 0 ? `${messageCount} unread` : 'No new messages'}
                </p>
              </div>
            </div>
            {messageCount > 0 && (
              <span className="h-5 w-5 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center">
                {messageCount}
              </span>
            )}
          </button>

          {/* Support Tickets */}
          <button
            onClick={() => handleNavigate('/my-tickets')}
            className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-lime-100 flex items-center justify-center">
                <Ticket className="h-4 w-4 text-lime-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-slate-900">Support Tickets</p>
                <p className="text-xs text-slate-500">
                  {ticketCount > 0 ? `${ticketCount} new response${ticketCount > 1 ? 's' : ''}` : 'No updates'}
                </p>
              </div>
            </div>
            {ticketCount > 0 && (
              <span className="h-5 w-5 rounded-full bg-lime-500 text-white text-xs flex items-center justify-center">
                {ticketCount}
              </span>
            )}
          </button>
        </div>
        
        {totalCount === 0 && (
          <div className="px-4 pb-4 text-center">
            <p className="text-xs text-slate-400">You're all caught up!</p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
